(() => {
  const init = (root) => {
    const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

    // ------------------------------
    // Quantity stepper (accessible)
    // ------------------------------
    const qtyInput = root.querySelector('input[name="quantity"]');
    let updateAtcTotal = () => { };
    const decBtn = root.querySelector('.frame-quantity-input-container .group');
    const incBtn = root.querySelector('.frame-quantity-input-container .plus-wrapper');

    const setQty = (value) => {
      if (!qtyInput) return;
      const min = Number(qtyInput.min || 1);
      const max = qtyInput.max ? Number(qtyInput.max) : Number.POSITIVE_INFINITY;
      const next = clamp(Math.round(value), min, max);
      qtyInput.value = String(next);
      updateAtcTotal();

      if (decBtn) decBtn.disabled = next <= min;
      if (incBtn) incBtn.disabled = next >= max;
    };

    if (qtyInput) {
      // Ensure valid value on load
      const initial = Number(qtyInput.value || 1);
      setQty(Number.isFinite(initial) ? initial : 1);

      qtyInput.addEventListener('change', () => {
        const v = Number(qtyInput.value);
        setQty(Number.isFinite(v) ? v : 1);
      });

      qtyInput.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setQty(Number(qtyInput.value) + 1);
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setQty(Number(qtyInput.value) - 1);
        }
      });
    }

    decBtn?.addEventListener('click', () => {
      if (!qtyInput) return;
      setQty(Number(qtyInput.value) - 1);
    });

    incBtn?.addEventListener('click', () => {
      if (!qtyInput) return;
      setQty(Number(qtyInput.value) + 1);
    });

    // ------------------------------
    // Vanilla gallery carousel + synced thumbnails
    // ------------------------------
    const galleryMain = root.querySelector('[data-gallery-main]');
    const track = galleryMain ? galleryMain.querySelector('.gallery-track') : null;
    const slides = track ? Array.from(track.querySelectorAll('.gallery-slide')) : [];
    const thumbs = Array.from(root.querySelectorAll('.gallery-thumbs .thumb'));
    const prevBtn = root.querySelector('.gallery-nav.prev');
    const nextBtn = root.querySelector('.gallery-nav.next');
    const totalSlides = slides.length;

    // Build variant ↔ slide index mapping
    const variantMap = new Map();
    const slideVariantMap = new Map();
    thumbs.forEach((thumb, i) => {
      const vid = thumb.getAttribute('data-variant-id');
      if (vid) {
        variantMap.set(vid, i);
        slideVariantMap.set(i, vid);
      }
    });

    let currentIndex = 0;
    let setActive = null;

    if (galleryMain && track && totalSlides > 0) {
      const initialIndex = parseInt(galleryMain.getAttribute('data-initial-index') || '0', 10);
      currentIndex = Math.max(0, Math.min(initialIndex, totalSlides - 1));

      // Position track to initial slide (no transition on first paint)
      track.style.transition = 'none';
      track.style.transform = `translateX(-${currentIndex * 100}%)`;
      // Re-enable transition after first paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          track.style.transition = '';
        });
      });

      // ── Core: go to slide ──
      function goToSlide(index, animate) {
        if (index < 0 || index >= totalSlides) return;
        // If jumping more than 1 slide, skip the transition to avoid sliding through
        const shouldAnimate = animate !== undefined ? animate : Math.abs(index - currentIndex) <= 1;
        if (!shouldAnimate) {
          track.style.transition = 'none';
        }
        currentIndex = index;
        track.style.transform = `translateX(-${currentIndex * 100}%)`;
        highlightThumb(currentIndex);
        if (!shouldAnimate) {
          // Force reflow, then restore transition
          track.offsetHeight;
          track.style.transition = '';
        }
      }

      // ── Thumb highlight + scroll into view ──
      function highlightThumb(index) {
        thumbs.forEach((t, i) => {
          const active = i === index;
          t.classList.toggle('is-active', active);
          if (active) {
            t.setAttribute('aria-current', 'true');
            t.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          } else {
            t.removeAttribute('aria-current');
          }
        });
      }

      // ── Arrow navigation ──
      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          goToSlide(currentIndex - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          goToSlide(currentIndex + 1);
        });
      }

      // ── Thumbnail clicks ──
      thumbs.forEach((btn, i) => {
        btn.addEventListener('click', () => goToSlide(i));
      });

      // ── Touch / pointer swipe ──
      // Allow vertical page scroll; only intercept clear horizontal swipes
      galleryMain.style.touchAction = 'pan-y';
      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let isDragging = false;
      let isHorizontal = null;
      const SWIPE_THRESHOLD = 40;
      const DIR_LOCK_THRESHOLD = 10;

      galleryMain.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        deltaX = 0;
        isDragging = true;
        isHorizontal = null;
        track.classList.add('is-dragging');
      }, { passive: true });

      galleryMain.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;

        // Lock direction on first significant move (10px threshold)
        if (isHorizontal === null && (Math.abs(dx) > DIR_LOCK_THRESHOLD || Math.abs(dy) > DIR_LOCK_THRESHOLD)) {
          isHorizontal = Math.abs(dx) > Math.abs(dy);
        }

        // If vertical or undecided, let the browser handle page scroll
        if (!isHorizontal) return;

        // Only prevent default for confirmed horizontal swipes
        e.preventDefault();
        deltaX = dx;
        const baseOffset = -currentIndex * 100;
        const dragPercent = (deltaX / galleryMain.offsetWidth) * 100;
        track.style.transform = `translateX(${baseOffset + dragPercent}%)`;
      }, { passive: false });

      galleryMain.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        track.classList.remove('is-dragging');

        if (isHorizontal && Math.abs(deltaX) > SWIPE_THRESHOLD) {
          if (deltaX < 0 && currentIndex < totalSlides - 1) {
            goToSlide(currentIndex + 1);
          } else if (deltaX > 0 && currentIndex > 0) {
            goToSlide(currentIndex - 1);
          } else {
            goToSlide(currentIndex); // snap back
          }
        } else {
          goToSlide(currentIndex); // snap back
        }
        deltaX = 0;
      }, { passive: true });

      // ── Keyboard navigation ──
      galleryMain.setAttribute('tabindex', '0');
      galleryMain.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goToSlide(currentIndex - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goToSlide(currentIndex + 1); }
      });

      // ── Public API for variant changes ──
      setActive = (nextIndex) => {
        const idx = Math.max(0, Math.min(nextIndex, totalSlides - 1));
        goToSlide(idx);
      };

      // Set initial state
      highlightThumb(currentIndex);
    }

    // ------------------------------
    // Details: sync aria-expanded for summary
    // ------------------------------
    root.querySelectorAll('details').forEach((d) => {
      const s = d.querySelector('summary');
      if (!s) return;
      s.setAttribute('aria-expanded', d.open ? 'true' : 'false');
      d.addEventListener('toggle', () => {
        s.setAttribute('aria-expanded', d.open ? 'true' : 'false');
      });
    });

    // ------------------------------
    // Variant selection and availability handling (color and non-color)
    // ------------------------------
    const colorRadios = root.querySelectorAll('.pdp-hero-color-radio');
    const tagRadios = root.querySelectorAll('.pdp-hero-variant-tag-radio');
    const addToCartBtn = root.querySelector('.pdp-hero-add-to-cart');
    const formButtons = root.querySelector('.pdp-hero-form-buttons');
    const engravingBlock = root.querySelector('.pdp-hero-engraving-block');
    const form = root.querySelector('.frame-12');
    const klaviyoTrigger = root.querySelector('.klaviyo-bis-trigger');
    const variantRadios = root.querySelector('variant-radios');
    let shouldOpenCartOnUpdate = false;
    let getCheckedKitBoxes = () => [];

    if (form) {
      form.addEventListener('submit', () => {
        const checked = getCheckedKitBoxes();
        shouldOpenCartOnUpdate = checked.length === 0;
      }, true);
    }

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (!shouldOpenCartOnUpdate) return;
        const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
        shouldOpenCartOnUpdate = false;
      });
    }

    // Get variant data from JSON script
    let variantsData = [];
    if (variantRadios) {
      const variantScript = variantRadios.querySelector('script[type="application/json"]');
      if (variantScript) {
        try {
          variantsData = JSON.parse(variantScript.textContent);
        } catch (e) {
          console.error('Failed to parse variant data', e);
        }
      }
    }

    // Get current variant from variant-radios component
    const getCurrentVariant = () => {
      if (variantRadios && variantRadios.currentVariant) {
        return {
          variantId: variantRadios.currentVariant.id.toString(),
          available: variantRadios.currentVariant.available,
          price: variantRadios.currentVariant.price,
          compareAtPrice: variantRadios.currentVariant.compare_at_price
        };
      }

      // Fallback: try to find from custom radios
      const selectedColorRadio = root.querySelector('.pdp-hero-color-radio:checked');
      if (selectedColorRadio && selectedColorRadio.dataset.variantId) {
        const variantId = selectedColorRadio.dataset.variantId;
        const variant = variantsData.find(v => v.id.toString() === variantId);
        if (variant) {
          return {
            variantId: variant.id.toString(),
            available: variant.available,
            price: variant.price,
            compareAtPrice: variant.compare_at_price
          };
        }
      }

      return null;
    };

    // Update price display
    const updatePrice = (variant) => {
      if (!variant || !variant.price) return;

      const priceElements = root.querySelectorAll('.price-item[data-price]');
      const priceContainer = root.querySelector('.price');

      // Format price
      const formatMoney = (cents) => {
        if (typeof window.formatMoney === 'function') {
          return window.formatMoney(cents);
        }
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cents / 100);
      };

      const formattedPrice = formatMoney(variant.price);
      const formattedComparePrice = variant.compareAtPrice ? formatMoney(variant.compareAtPrice) : null;

      // Update price elements
      priceElements.forEach((el) => {
        if (el.classList.contains('price-item--sale') || el.classList.contains('price-item--regular')) {
          el.textContent = formattedPrice;
          el.setAttribute('data-price', variant.price);
        }
      });

      // Update compare at price if on sale
      const comparePriceEl = root.querySelector('.price-item--regular[data-price]');
      if (comparePriceEl && variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        const compareAtEl = comparePriceEl.closest('.price__sale')?.querySelector('s');
        if (compareAtEl) {
          compareAtEl.textContent = formattedComparePrice;
          compareAtEl.setAttribute('data-price', variant.compareAtPrice);
        }
        if (priceContainer) {
          priceContainer.classList.add('price--on-sale');
        }
      } else {
        if (priceContainer) {
          priceContainer.classList.remove('price--on-sale');
        }
      }
    };

    // Sync custom radios with hidden variant-radios
    const syncVariantRadios = () => {
      if (!variantRadios) return;

      const selectedColorRadio = root.querySelector('.pdp-hero-color-radio:checked');
      const selectedTagRadios = root.querySelectorAll('.pdp-hero-variant-tag-radio:checked');

      // Get option names from our custom UI
      const selectedOptions = {};

      // Get color option
      if (selectedColorRadio) {
        const colorFieldset = selectedColorRadio.closest('[data-color-option]');
        if (colorFieldset) {
          const colorLegend = colorFieldset.querySelector('legend');
          if (colorLegend) {
            const optionName = colorLegend.textContent.replace('Select ', '').trim();
            selectedOptions[optionName] = selectedColorRadio.value;
          }
        }
      }

      // Get non-color options
      selectedTagRadios.forEach((tagRadio) => {
        const tagContainer = tagRadio.closest('.pdp-hero-non-color-options');
        if (tagContainer) {
          const tagFieldset = tagContainer.querySelector('fieldset');
          if (tagFieldset) {
            const tagLegend = tagFieldset.querySelector('legend');
            if (tagLegend) {
              const optionName = tagLegend.textContent.replace('Select ', '').trim();
              selectedOptions[optionName] = tagRadio.value;
            }
          }
        }
      });

      // Update hidden variant-radios inputs
      const variantRadioInputs = variantRadios.querySelectorAll('input[type="radio"]');
      variantRadioInputs.forEach((input) => {
        const optionName = input.name.replace('options[', '').replace(']', '');
        const inputValue = input.value;

        if (selectedOptions[optionName] === inputValue && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    };

    const updateButtonVisibility = (isAvailable, variantId) => {
      // Update hidden variant input for form submission and Klaviyo
      // The input should be inside the form, with class 'product-variant-id'
      const variantInput = form ? form.querySelector('input.product-variant-id[name="id"]') : null;
      if (variantInput && variantId) {
        variantInput.value = variantId;
        // The product-form web component will enable this on form submit
        // But we need to make sure it's set correctly
      }

      // Update quantity display visibility
      const qtyContainer = root.querySelector('.frame-quantity-input-container');
      if (qtyContainer) {
        qtyContainer.style.display = isAvailable ? '' : 'none';
      }

      // Hide/show entire form buttons container and engraving block when out of stock
      if (formButtons) {
        formButtons.style.display = isAvailable ? '' : 'none';
      }
      if (engravingBlock) {
        engravingBlock.style.display = isAvailable ? '' : 'none';
      }

      if (isAvailable) {
        // Show add to cart, hide back in stock
        if (addToCartBtn) {
          addToCartBtn.style.display = '';
          addToCartBtn.disabled = false;
          // Update button text
          const buttonText = addToCartBtn.querySelector('.SHOP-THE-COLLECTION');
          if (buttonText) {
            buttonText.textContent = 'ADD TO CART';
          }
        }
        if (klaviyoTrigger) {
          klaviyoTrigger.style.display = 'none';
        }
      } else {
        // Hide add to cart, show back in stock
        if (addToCartBtn) {
          addToCartBtn.style.display = 'none';
          addToCartBtn.disabled = true;
        }
        if (klaviyoTrigger) {
          klaviyoTrigger.style.display = '';
        }
      }
    };

    // Update gallery image based on variant
    const updateGalleryForVariant = (variantId) => {
      if (!galleryMain || !thumbs.length || !setActive) return;

      // Check if variant has an associated image
      if (variantId && variantMap.has(variantId)) {
        const slideIndex = variantMap.get(variantId);
        if (slideIndex !== undefined && slideIndex !== null) {
          setActive(slideIndex, { focusThumb: false });
          return;
        }
      }

      // Variant doesn't have an associated image - show main/featured image
      // Find the main image (first image without variant ID, or index 0)
      let mainIndex = 0;
      for (let i = 0; i < thumbs.length; i++) {
        if (!thumbs[i].getAttribute('data-variant-id')) {
          mainIndex = i;
          break;
        }
      }
      setActive(mainIndex, { focusThumb: false });
    };

    // Update URL with variant parameter while preserving other params
    const updateVariantURL = (variantId) => {
      if (!variantId || !variantRadios || !variantRadios.dataset.url) return;

      const url = new URL(window.location.href);
      // Set or replace the variant parameter
      url.searchParams.set('variant', variantId);
      // Update URL without page reload
      window.history.replaceState({}, '', url.toString());
    };

    const handleVariantChange = () => {
      // Sync with hidden variant-radios first
      syncVariantRadios();

      // Wait a bit for variant-radios to update, then get current variant
      setTimeout(() => {
        const variant = getCurrentVariant();
        if (variant) {
          updateButtonVisibility(variant.available, variant.variantId);
          updatePrice(variant);
          updateVariantURL(variant.variantId);
          updateGalleryForVariant(variant.variantId);

          // Dispatch custom event for variant change
          const variantChangeEvent = new CustomEvent('variant-change', {
            detail: {
              variantId: variant.variantId ? parseInt(variant.variantId, 10) : null,
              available: variant.available
            },
            bubbles: true
          });
          root.dispatchEvent(variantChangeEvent);
        }
      }, 100);
    };

    // Override variant-radios updateURL to preserve other params
    if (variantRadios && variantRadios.updateURL) {
      const originalUpdateURL = variantRadios.updateURL.bind(variantRadios);
      variantRadios.updateURL = function () {
        if (this.currentVariant && this.dataset.updateUrl !== 'false') {
          updateVariantURL(this.currentVariant.id.toString());
        }
      };
    }

    // Listen to variant-radios changes for price and URL updates
    if (variantRadios) {
      // Listen for the change event that variant-radios fires
      variantRadios.addEventListener('change', () => {
        const variant = getCurrentVariant();
        if (variant) {
          updatePrice(variant);
          updateButtonVisibility(variant.available, variant.variantId);
          updateVariantURL(variant.variantId);
          updateGalleryForVariant(variant.variantId);
        }
      });
    }

    // Update tag bubble selected states
    const updateTagBubbleStates = () => {
      root.querySelectorAll('.pdp-hero-variant-tag').forEach((tag) => {
        const radio = root.querySelector(`#${tag.getAttribute('for')}`);
        if (radio && radio.checked) {
          tag.classList.add('pdp-hero-variant-tag--selected');
        } else {
          tag.classList.remove('pdp-hero-variant-tag--selected');
        }
      });
    };

    // Initialize button visibility and price based on selected variant
    updateTagBubbleStates();

    // Sync variant-radios on initial load and get initial variant
    syncVariantRadios();
    setTimeout(() => {
      const variant = getCurrentVariant();
      if (variant) {
        updateButtonVisibility(variant.available, variant.variantId);
        updatePrice(variant);
        updateGalleryForVariant(variant.variantId);
      }
    }, 100);

    // Handle color radio changes
    if (colorRadios.length > 0) {
      colorRadios.forEach((radio) => {
        radio.addEventListener('change', (e) => {
          if (!e.target.checked) return;
          handleVariantChange();
        });
      });
    }

    // Handle tag radio changes
    if (tagRadios.length > 0) {
      tagRadios.forEach((radio) => {
        radio.addEventListener('change', (e) => {
          if (!e.target.checked) return;
          updateTagBubbleStates();
          handleVariantChange();
        });
      });
    }

    // ------------------------------
    // Compatible Kits (variant addons)
    // ------------------------------
    const compatibleKits = root.querySelector('[data-compatible-kits]');
    if (compatibleKits) {
      compatibleKits.setAttribute('hidden', '');
      console.log('[Compatible Kits] init', {
        hasToken: Boolean(root.dataset.storefrontApiToken),
        shop: typeof window.Shopify !== 'undefined' ? window.Shopify.shop : null
      });
      const groups = root.querySelectorAll('.pdp-hero-compatible-kits-group');
      const formatMoney = (cents) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(cents / 100).replace('.00', '');
      };

      let activeVariantId = null;
      const updateCompatibleKitsContainerVisibility = () => {
        const hasVisibleGroup = Array.from(groups).some((group) => {
          return group.dataset.kitsLoaded === '1' && !group.hasAttribute('hidden');
        });
        if (hasVisibleGroup) {
          compatibleKits.removeAttribute('hidden');
        } else {
          compatibleKits.setAttribute('hidden', '');
        }
      };

      const updateCompatibleKitsVisibility = (currentVariantId) => {
        const variantIdStr = currentVariantId ? String(currentVariantId) : '';
        groups.forEach((group) => {
          const groupVariantId = group.getAttribute('data-variant-id');
          const isLoaded = group.dataset.kitsLoaded === '1';
          if (groupVariantId === variantIdStr && isLoaded) {
            group.removeAttribute('hidden');
          } else {
            group.setAttribute('hidden', '');
          }
        });
        updateCompatibleKitsContainerVisibility();
        // Debug: show current variant + group ids
        let debugEl = root.querySelector('[data-compatible-kits-debug]');
        if (!debugEl && compatibleKits) {
          debugEl = document.createElement('div');
          debugEl.setAttribute('data-compatible-kits-debug', '');
          debugEl.style.cssText = 'margin:0.5rem 0;padding:0.5rem;background:#ffecec;font-size:12px;';
          compatibleKits.prepend(debugEl);
        }
        if (debugEl) {
          const groupIds = Array.from(groups).map((g) => g.getAttribute('data-variant-id')).join(', ');
        }
      };

      const addToCartBtn = root.querySelector('.pdp-hero-add-to-cart');
      const addToCartText = addToCartBtn ? addToCartBtn.querySelector('.SHOP-THE-COLLECTION') : null;
      const defaultAtcText = addToCartText ? addToCartText.textContent.trim() : '';
      const isMainAtcAvailable = () => {
        if (!addToCartBtn) return false;
        if (addToCartBtn.disabled) return false;
        return addToCartBtn.offsetParent !== null;
      };
      const updateKitCheckboxVisibility = () => {
        const show = isMainAtcAvailable();
        root.querySelectorAll('.pdp-hero-compatible-kits-item').forEach((item) => {
          item.classList.toggle('pdp-hero-compatible-kits-item--no-checkbox', !show);
        });
        root.querySelectorAll('.pdp-hero-compatible-kits-checkbox').forEach((cb) => {
          if (!show) {
            cb.checked = false;
            cb.disabled = true;
            cb.style.display = 'none';
            return;
          }
          cb.style.display = '';
          cb.disabled = cb.getAttribute('data-available') !== 'true';
        });
      };
      getCheckedKitBoxes = () => Array.from(root.querySelectorAll('.pdp-hero-compatible-kits-checkbox:checked'));
      const getBaseVariantFallback = () => {
        const variantInput = form ? form.querySelector('input.product-variant-id[name="id"]') : null;
        const priceEl = root.querySelector('.price-item--sale[data-price], .price-item--regular[data-price]');
        const price = priceEl ? parseInt(priceEl.getAttribute('data-price') || '0', 10) : 0;
        return variantInput && variantInput.value ? { variantId: variantInput.value, price, available: true } : null;
      };
      const updateMainAtcTotal = () => {
        if (!isMainAtcAvailable()) return;
        if (!addToCartText) return;
        const variant = getCurrentVariant() || getBaseVariantFallback();
        if (!variant || variant.available === false) return;
        const checked = getCheckedKitBoxes();
        if (checked.length === 0) {
          addToCartText.textContent = defaultAtcText || 'ADD TO CART';
          return;
        }
        const qtyInput = root.querySelector('input[name="quantity"]');
        const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value || '1', 10)) : 1;
        let totalCents = parseInt(variant.price || 0, 10) * qty;
        checked.forEach((cb) => {
          if (cb.getAttribute('data-available') === 'true') {
            totalCents += parseInt(cb.getAttribute('data-price') || '0', 10) * qty;
          }
        });
        addToCartText.textContent = `ADD TO CART • ${formatMoney(totalCents)}`;
      };
      updateAtcTotal = updateMainAtcTotal;

      compatibleKits.addEventListener('change', (e) => {
        if (!isMainAtcAvailable()) return;
        if (e.target.matches('.pdp-hero-compatible-kits-checkbox')) {
          updateMainAtcTotal();
        }
      });
      const qtyInput = root.querySelector('input[name="quantity"]');
      if (qtyInput) {
        qtyInput.addEventListener('change', updateMainAtcTotal);
        qtyInput.addEventListener('input', updateMainAtcTotal);
      }

      root.addEventListener('variant-change', (e) => {
        const variantId = e.detail?.variantId;
        activeVariantId = variantId || null;
        updateCompatibleKitsVisibility(variantId);
        updateKitCheckboxVisibility();
        setTimeout(updateMainAtcTotal, 50);
      });

      const initVariant = getCurrentVariant();
      if (initVariant) {
        activeVariantId = initVariant.variantId;
        updateCompatibleKitsVisibility(initVariant.variantId);
      } else {
        const variantInput = form ? form.querySelector('input.product-variant-id[name="id"]') : null;
        if (variantInput && variantInput.value) {
          activeVariantId = variantInput.value;
          updateCompatibleKitsVisibility(variantInput.value);
        }
      }
      setTimeout(updateMainAtcTotal, 150);
      updateKitCheckboxVisibility();

      // When kits are selected, add all items via main ATC
      const formEl = root.querySelector('form[data-type="add-to-cart-form"]');
      if (formEl) {
        formEl.addEventListener('submit', async (e) => {
          const checked = getCheckedKitBoxes();
          if (checked.length === 0) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          const variant = getCurrentVariant() || getBaseVariantFallback();
          if (!variant || variant.available === false) return;
          const qtyInput = root.querySelector('input[name="quantity"]');
          const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value || '1', 10)) : 1;
          const items = [{ id: variant.variantId, quantity: qty }];
          checked.forEach((cb) => {
            if (cb.getAttribute('data-available') === 'true') {
              const vid = cb.getAttribute('data-variant-id');
              if (vid) items.push({ id: vid, quantity: qty });
            }
          });
          if (!addToCartBtn) return;
          addToCartBtn.disabled = true;
          if (addToCartText) addToCartText.textContent = 'Adding…';
          try {
            const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
            const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            if (cartDrawer) cartDrawer.setActiveElement?.(document.activeElement);
            const config = typeof fetchConfig === 'function'
              ? fetchConfig('javascript')
              : { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' } };
            config.headers = config.headers || {};
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            const formData = new FormData();
            items.forEach((item, index) => {
              formData.append(`items[${index}][id]`, item.id);
              formData.append(`items[${index}][quantity]`, item.quantity);
            });
            if (cartDrawer && cartDrawer.getSectionsToRender) {
              const sectionsToRender = cartDrawer.getSectionsToRender();
              const sectionIds = sectionsToRender.map((section) => section.id);
              formData.append('sections', sectionIds.join(','));
              formData.append('sections_url', window.location.pathname);
            }
            config.body = formData;
            const response = await fetch(cartAddUrl, config);
            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.description || errorData.message || 'Failed to add items to cart');
            }
            const data = await response.json();
            if (data.status) {
              throw new Error(data.description || 'Failed to add items to cart');
            }
            if (!cartDrawer) {
              window.location = window.routes?.cart_url || '/cart';
              return;
            }
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
            }
            cartDrawer.renderContents(data);
            if (cartDrawer.open) setTimeout(() => cartDrawer.open(), 100);
            if (addToCartText) addToCartText.textContent = 'Added';
            setTimeout(() => {
              if (addToCartText) addToCartText.textContent = defaultAtcText || 'ADD TO CART';
              addToCartBtn.disabled = false;
              updateMainAtcTotal();
            }, 1500);
          } catch (err) {
            console.error(err);
            if (addToCartText) addToCartText.textContent = defaultAtcText || 'ADD TO CART';
            addToCartBtn.disabled = false;
            updateMainAtcTotal();
          }
        }, true);
      }

      // JS fallback: resolve variant GIDs via Storefront API and render kit items
      const storefrontToken = root.dataset.storefrontApiToken;
      const shop = typeof window.Shopify !== 'undefined' && window.Shopify.shop ? window.Shopify.shop : null;
      if (storefrontToken && shop) {
        console.log('[Compatible Kits] Storefront API enabled', { shop, groupCount: groups.length });
        const loadingGroups = Array.from(root.querySelectorAll('.pdp-hero-compatible-kits-group')).filter((g) => g.querySelector('.pdp-hero-compatible-kits-items-loading'));
        console.log('[Compatible Kits] groups with loading placeholders', loadingGroups.length);
        const apiUrl = `https://${shop}/api/2024-01/graphql.json`;
        const fetchVariantByGid = async (gid) => {
          try {
            const res = await fetch(apiUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': storefrontToken
              },
              body: JSON.stringify({
                query: `query getVariant($id: ID!) { node(id: $id) { ... on ProductVariant { id title availableForSale image { url } price { amount } product { title handle featuredImage { url } } } } }`,
                variables: { id: gid }
              })
            });
            const json = await res.json();
            if (json.errors) {
              console.error('[Compatible Kits] Storefront API errors:', json.errors);
            }
            return json.data?.node || null;
          } catch (err) {
            console.error('[Compatible Kits] Storefront API request failed:', err);
            return null;
          }
        };
        const formatMoneyFallback = (amountStr) => {
          const n = parseFloat(amountStr, 10);
          if (typeof window.formatMoney === 'function' && Number.isFinite(n)) {
            return window.formatMoney(Math.round(n * 100));
          }
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(n);
        };
        loadingGroups.forEach(async (group) => {
          const gidsScript = group.querySelector('script.pdp-hero-compatible-kits-gids');
          const gidsAttr = gidsScript ? gidsScript.textContent : '';
          const itemsContainer = group.querySelector('.pdp-hero-compatible-kits-items');
          const loadingEl = group.querySelector('.pdp-hero-compatible-kits-items-loading');
          if (!itemsContainer || !loadingEl) return;
          let gids = [];
          try {
            const parsed = JSON.parse(gidsAttr || '[]');
            gids = Array.isArray(parsed) ? parsed : [];
          } catch (_) { }
          if (gids.length === 0 && gidsAttr) {
            const matches = gidsAttr.match(/gid:\/\/shopify\/ProductVariant\/\d+/g);
            if (matches) gids = matches;
          }
          console.log('[Compatible Kits] raw gids payload', gidsAttr);
          console.log('[Compatible Kits] group gids', gids);
          if (gids.length === 0) {
            loadingEl.textContent = 'No kits found.';
            return;
          }
          const fragment = document.createDocumentFragment();
          let renderedCount = 0;
          for (const gid of gids) {
            if (!gid || typeof gid !== 'string') continue;
            const variant = await fetchVariantByGid(gid);
            if (!variant) continue;
            const variantId = variant.id ? String(variant.id).split('/').pop() : '';
            const priceCents = Math.round(parseFloat(variant.price?.amount || 0, 10) * 100);
            const available = !!variant.availableForSale;
            const imgUrl = variant.image?.url || variant.product?.featuredImage?.url || '';
            const productHandle = variant.product?.handle || '';
            const productUrl = productHandle ? `${window.location.origin}/products/${productHandle}` : window.location.origin;
            const baseTitle = variant.product?.title || '';
            const variantTitle = variant.title || '';
            const title = baseTitle
              ? (variantTitle && variantTitle !== 'Default Title'
                ? `${baseTitle}: ${variantTitle}`
                : baseTitle)
              : variantTitle;
            const priceFormatted = formatMoneyFallback(variant.price?.amount || '0');
            const safeTitle = title.replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const safeAlt = title.replace(/"/g, '&quot;');
            const safeImgUrl = imgUrl.startsWith('//') ? `https:${imgUrl}` : imgUrl;
            const imgHtml = imgUrl
              ? `<img src="${safeImgUrl}" width="48" height="48" alt="${safeAlt}" loading="lazy">`
              : '<span class="pdp-hero-compatible-kits-item-thumb-placeholder"></span>';
            const label = document.createElement('label');
            label.className = 'pdp-hero-compatible-kits-item' + (available ? '' : ' pdp-hero-compatible-kits-item--out-of-stock');
            const checkboxHtml = `<input type="checkbox" class="pdp-hero-compatible-kits-checkbox" data-variant-id="${variantId}" data-price="${priceCents}" data-available="${available}"${available ? '' : ' disabled'}>`;
            label.innerHTML = `
              ${checkboxHtml}
              <span class="pdp-hero-compatible-kits-item-thumb">
                ${imgHtml}
              </span>
              <span class="pdp-hero-compatible-kits-item-info">
                <a href="${productUrl}" class="pdp-hero-compatible-kits-item-title" target="_blank" rel="noopener">${safeTitle}</a>
                <span class="pdp-hero-compatible-kits-item-price">${priceFormatted}</span>
              </span>
            `;
            fragment.appendChild(label);
            renderedCount += 1;
          }
          loadingEl.remove();
          itemsContainer.appendChild(fragment);
          if (renderedCount > 0) {
            group.dataset.kitsLoaded = '1';
          }
          updateCompatibleKitsVisibility(activeVariantId);
          updateMainAtcTotal();
        });
      } else if (compatibleKits) {
        compatibleKits.querySelectorAll('.pdp-hero-compatible-kits-items-loading').forEach((el) => {
          el.textContent = 'Missing Storefront API token.';
        });
        compatibleKits.setAttribute('hidden', '');
      }

    }
  };

  document.querySelectorAll('[data-pdp-hero]').forEach((root) => {
    if (root.dataset.pdpHeroInit === '1') return;
    root.dataset.pdpHeroInit = '1';
    init(root);
  });

  /* ═══════ Sticky Add-to-Cart Bar ═══════ */
  const stickyBar = document.querySelector('[data-pdp-sticky-atc]');
  if (stickyBar) {
    const sQtyInput = stickyBar.querySelector('[data-sticky-qty-input]');
    const sDecBtn = stickyBar.querySelector('[data-sticky-qty-dec]');
    const sIncBtn = stickyBar.querySelector('[data-sticky-qty-inc]');
    const sAtcBtn = stickyBar.querySelector('[data-sticky-atc-btn]');
    const sAtcText = stickyBar.querySelector('[data-sticky-atc-text]');
    const sEngraveBtn = stickyBar.querySelector('[data-sticky-engrave-btn]');

    /* Find the original form elements */
    const heroRoot = document.querySelector('[data-pdp-hero]');
    const origQtyInput = heroRoot ? heroRoot.querySelector('input[name="quantity"]') : null;
    const origAtcBtn = heroRoot ? heroRoot.querySelector('.pdp-hero-add-to-cart') : null;
    const origZakeke = heroRoot ? heroRoot.querySelector('.pdp-hero-engraving-block .zakeke-product-button') : null;

    /* Hide engrave button if no Zakeke block */
    if (!origZakeke && sEngraveBtn) {
      sEngraveBtn.style.display = 'none';
    }

    /* QTY stepper – sync with original */
    function syncQty(newVal) {
      const v = Math.max(1, parseInt(newVal) || 1);
      sQtyInput.value = v;
      if (origQtyInput) origQtyInput.value = v;
    }
    sDecBtn.addEventListener('click', () => syncQty(parseInt(sQtyInput.value) - 1));
    sIncBtn.addEventListener('click', () => syncQty(parseInt(sQtyInput.value) + 1));
    sQtyInput.addEventListener('change', () => syncQty(sQtyInput.value));

    /* Keep sticky qty in sync if original changes */
    if (origQtyInput) {
      const qtyObs = new MutationObserver(() => { sQtyInput.value = origQtyInput.value; });
      qtyObs.observe(origQtyInput, { attributes: true, attributeFilter: ['value'] });
      origQtyInput.addEventListener('change', () => { sQtyInput.value = origQtyInput.value; });
    }

    /* ATC – click the original submit button */
    sAtcBtn.addEventListener('click', () => {
      if (origAtcBtn && !origAtcBtn.disabled) {
        syncQty(sQtyInput.value);
        origAtcBtn.click();
      }
    });

    /* Mirror disabled / sold-out state */
    function mirrorAtcState() {
      if (!origAtcBtn) return;
      const soldOut = origAtcBtn.disabled;
      sAtcBtn.disabled = soldOut;
      sAtcText.textContent = soldOut ? 'SOLD OUT' : 'ADD TO CART';
    }
    mirrorAtcState();
    if (origAtcBtn) {
      const atcObs = new MutationObserver(mirrorAtcState);
      atcObs.observe(origAtcBtn, { attributes: true, attributeFilter: ['disabled'] });
    }

    /* Engrave – click the original Zakeke button */
    if (sEngraveBtn) {
      sEngraveBtn.addEventListener('click', () => {
        /* Zakeke button may load late, re-query */
        const zBtn = document.querySelector('.pdp-hero-engraving-block .zakeke-product-button');
        if (zBtn) zBtn.click();
      });
    }

    /* ── Sticky bar color selector ── */
    const stickyColors = stickyBar.querySelector('[data-sticky-colors]');
    if (stickyColors) {
      const stickyToggle = stickyColors.querySelector('[data-sticky-color-toggle]');
      const stickyExpanded = stickyColors.querySelector('[data-sticky-colors-expanded]');
      const stickyDots = stickyColors.querySelectorAll('[data-sticky-color-value]');
      const stickyPreview = stickyColors.querySelector('.pdp-sticky-atc__color-preview');

      /* Mobile toggle */
      if (stickyToggle) {
        stickyToggle.addEventListener('click', (e) => {
          e.stopPropagation();
          stickyColors.classList.toggle('is-expanded');
        });
        /* Close when clicking outside */
        document.addEventListener('click', (e) => {
          if (!stickyColors.contains(e.target)) {
            stickyColors.classList.remove('is-expanded');
          }
        });
      }

      /* Click a sticky dot → click the matching hero radio */
      stickyDots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const colorValue = dot.dataset.stickyColorValue;
          /* Update active state on sticky dots */
          stickyDots.forEach(d => d.classList.remove('is-active'));
          dot.classList.add('is-active');
          /* Update mobile preview swatch */
          if (stickyPreview) {
            stickyPreview.style.backgroundColor = dot.style.backgroundColor;
          }
          /* Click the hero form radio */
          if (heroRoot) {
            const heroRadios = heroRoot.querySelectorAll('.pdp-hero-color-radio');
            heroRadios.forEach((radio) => {
              if (radio.value === colorValue && !radio.checked) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
            /* Update the hero color label */
            const heroLabel = heroRoot.querySelector('[data-selected-color]');
            if (heroLabel) heroLabel.textContent = colorValue;
          }
          /* Collapse on mobile after selection */
          stickyColors.classList.remove('is-expanded');
        });
      });

      /* Sync: hero form → sticky bar */
      if (heroRoot) {
        const heroRadios = heroRoot.querySelectorAll('.pdp-hero-color-radio');
        heroRadios.forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            const val = radio.value;
            stickyDots.forEach(d => {
              d.classList.toggle('is-active', d.dataset.stickyColorValue === val);
              if (d.dataset.stickyColorValue === val && stickyPreview) {
                stickyPreview.style.backgroundColor = d.style.backgroundColor;
              }
            });
          });
        });
      }
    }

    /* Desktop: IntersectionObserver to show after scrolling past hero */
    const isMobile = () => window.matchMedia('(max-width: 640px)').matches;

    if (heroRoot) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (isMobile()) return; /* CSS handles mobile */
          if (entry.isIntersecting) {
            stickyBar.classList.remove('is-visible');
          } else {
            stickyBar.classList.add('is-visible');
          }
        });
      }, { threshold: 0.1 });
      observer.observe(heroRoot);
    }
  }

})();
